import { ComponentType, PointerEvent } from 'react';
import { useSpring } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
//
import { Product } from '../../../data/products';
import { FONT, STAGE, TYPE } from '../../../theme/stage';
import { ANCHOR, NEXT_PRODUCT, productNumber } from '../copy';
import Accent from '../primitives/Accent';
import ActionLink from '../primitives/ActionLink';
import useStageMode from '../useStageMode';
import NextArt from './NextArt';
import TondTondArt, { ArtProps } from './TondTondArt';
import WorldstageArt from './WorldstageArt';

// ----------------------------------------------------------------------

type Props = { variant: 'live'; product: Product } | { variant: 'next'; index: number };

const ARTS: Partial<Record<string, ComponentType<ArtProps>>> = {
  tondtond: TondTondArt,
  worldstage: WorldstageArt,
};

const FAN_SPRING = { stiffness: 220, damping: 18 };

const CARD = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: { md: 520 },
  p: { xs: '24px', sm: '32px' },
  border: `1px solid ${STAGE.line}`,
  borderRadius: { xs: '40px', sm: '48px' },
  bgcolor: STAGE.surface,
  containerType: 'inline-size',
  containerName: 'product',
} as const;

const ART = {
  position: 'relative',
  flexShrink: 0,
  aspectRatio: '4 / 3',
  borderRadius: '16px',
  overflow: 'hidden',
} as const;

const ROW = {
  ...TYPE.label,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px 16px',
  m: { xs: '24px 0 0', sm: '28px 0 0' },
  color: STAGE.mute,
} as const;

const STATUS = { display: 'inline-flex', alignItems: 'center', gap: '8px' } as const;

const MARK = { flexShrink: 0, width: 8, height: 8, borderRadius: '50%' } as const;

const TITLE = {
  m: '20px 0 0',
  fontFamily: FONT.sans,
  fontWeight: 500,
  fontSize: { xs: 32, sm: 40 },
  lineHeight: 1.05,
  letterSpacing: { xs: '-0.025em', sm: '-0.03em' },
  color: STAGE.ink,
} as const;

const BODY = { ...TYPE.body, m: '12px 0 0', color: STAGE.ink2, textWrap: 'pretty' } as const;

function LiveCard({ product }: { product: Product }) {
  const { finePointer, reduced } = useStageMode();

  const hover = finePointer && !reduced;

  const fan = useSpring(0, FAN_SPRING);

  const id = ANCHOR.product(product.id);

  const titleId = `${id}-title`;

  const Art = ARTS[product.id];

  return (
    <Box
      component="article"
      id={id}
      aria-labelledby={titleId}
      onPointerEnter={
        hover
          ? (event: PointerEvent<HTMLElement>) => event.pointerType !== 'touch' && fan.set(1)
          : undefined
      }
      onPointerLeave={hover ? () => fan.set(0) : undefined}
      sx={CARD}
    >
      <Box aria-hidden sx={{ ...ART, bgcolor: STAGE.surface2 }}>
        {Art && <Art fan={fan} animated={hover} />}
      </Box>

      <Box component="p" sx={ROW}>
        <span>{productNumber(product.index)}</span>{' '}
        <Box component="span" sx={{ ...STATUS, color: STAGE.ink2 }}>
          <Box component="span" aria-hidden sx={{ ...MARK, bgcolor: product.brand.main }} />
          {product.statusLabel}
        </Box>
      </Box>

      <Box component="h3" id={titleId} sx={TITLE}>
        {product.name}
      </Box>

      <Box component="p" sx={BODY}>
        {product.category}
      </Box>

      <Box component="p" sx={{ ...BODY, mt: '8px' }}>
        {product.summary}
      </Box>

      <Box sx={{ display: 'flex', mt: 'auto', pt: '28px' }}>
        <ActionLink
          variant="brand"
          brand={product.brand}
          href={product.primaryCta.href}
          ariaLabel={product.primaryCta.ariaLabel}
          sx={{ flex: { xs: 1, sm: 'none' } }}
        >
          {product.primaryCta.label}
        </ActionLink>
      </Box>
    </Box>
  );
}

function NextCard({ index }: { index: number }) {
  const titleId = ANCHOR.product('next');

  return (
    <Box
      component="article"
      aria-labelledby={titleId}
      sx={{
        ...CARD,
        display: { xs: 'flex', md: 'grid', lg: 'flex' },
        gridTemplateColumns: { md: 'repeat(2, minmax(0, 1fr))' },
        columnGap: { md: '90px' },
        alignItems: { md: 'center' },
        minHeight: { md: 0, lg: 520 },
      }}
    >
      <Box aria-hidden sx={{ ...ART, bgcolor: STAGE.surface2 }}>
        <NextArt />
      </Box>

      <div>
        <Box component="p" sx={{ ...ROW, mt: { md: 0, lg: '28px' } }}>
          <span>{productNumber(index)}</span>{' '}
          <Box component="span" sx={STATUS}>
            <Box
              component="span"
              aria-hidden
              sx={{ ...MARK, border: `1.5px solid ${STAGE.lineStrong}` }}
            />
            {NEXT_PRODUCT.status}
          </Box>
        </Box>

        <Box component="h3" id={titleId} sx={TITLE}>
          <Accent opsz={40}>{NEXT_PRODUCT.title}</Accent>
        </Box>

        <Box component="p" sx={BODY}>
          {NEXT_PRODUCT.line}
        </Box>
      </div>
    </Box>
  );
}

export default function ProductCard(props: Props) {
  return props.variant === 'live' ? (
    <LiveCard product={props.product} />
  ) : (
    <NextCard index={props.index} />
  );
}
